<template>
    <pendo-app
        class="vue-wrapper"
        :class="{
            'side-panel-open': panelOpen,
            'vue-wrapper--framed': isAppFrameActive
        }">
        <topbar v-if="isOnGlobalSettings" />
        <pendo-page
            ref="pendoPage"
            background-color="var(--surface-default-background)">
            <router-view-wrapper ref="routerViewWrapper">
                <ancient-app-version-banner
                    v-if="showAncientAppVersionBanner"
                    :days-out-of-date="daysOutOfDate" />
                <template v-if="!isOnTrialExpiredRoute">
                    <install-snippet-banner v-if="showInstallSnippetBanner" />
                    <sample-data-banner v-if="authenticated" />
                </template>
                <router-view :key="routerViewKey" />
            </router-view-wrapper>
        </pendo-page>
        <transition name="slide-panel">
            <side-panel-wrapper
                :is-open="panelOpen"
                :render-content="panelContent"
                class="side-panel-wrapper" />
        </transition>
    </pendo-app>
</template>

<script>
import { mapState as mapPiniaState, mapActions as mapPiniaActions } from 'pinia';
import { useLeftNavStore } from '@/stores/left-nav.store';
import { canHaveGlobalBanner } from '@/global-banner/global-banner.utils';
import InstallSnippetBanner from '../free/install-snippet-banner/install-snippet-banner.vue';
import SampleDataBanner from '../free/sample-data-banner/sample-data-banner.vue';
import { PendoApp, PendoPage } from '@pendo/components-vue3';
import SidePanelWrapper from './side-panel-wrapper/side-panel-wrapper.vue';
import { EventBus } from '@/utils/event-bus.js';
import { isOnGlobalSettingsPage, isOnReplayPlayerPage, isOnListenPage, isOnDataSyncPage, shouldShowNavItems } from '@/vue-router/route.utils';
import Topbar from '@/admin/topbar/topbar.vue';
import { useResizeObserver } from '@vueuse/core';
import { useTemplateRef, onMounted, watch } from 'vue';
import { useFixedStyles } from '@/common/use/use-fixed-styles/use-fixed-styles';
import { tabbable } from 'tabbable';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import RouterViewWrapper from './router-view-wrapper.vue';
import AncientAppVersionBanner from './ancient-app-version-banner.vue';
import { useAuthStore } from '@/stores/auth.store';

export default {
    name: 'VueWrapper',
    components: {
        InstallSnippetBanner,
        SampleDataBanner,
        PendoApp,
        PendoPage,
        RouterViewWrapper,
        SidePanelWrapper,
        AncientAppVersionBanner,
        Topbar,
    },
    emits: ['unmount'],
    setup () {
        const pendoPage = useTemplateRef('pendoPage');
        const { drawerStyles, affixStyles, setPendoPageRightMargin } = useFixedStyles();

        watch(
            () => drawerStyles.value,
            ({ top, right, bottom, pushDrawerShellChrome, pushDrawerShellBorderRadiusPx }) => {
                const root = document.querySelector(':root');
                root.style.setProperty('--drawer-top-position', `${top}px`);
                root.style.setProperty('--drawer-right-position', `${right}px`);
                root.style.setProperty('--drawer-bottom-position', `${bottom}px`);
                root.style.setProperty(
                    '--push-drawer-border-bottom-right-radius',
                    pushDrawerShellChrome ? `${pushDrawerShellBorderRadiusPx}px` : '0px',
                );
            },
            { immediate: true },
        );

        watch(
            () => affixStyles.value.fixedStyles.top,
            (top) => {
                const root = document.querySelector(':root');
                root.style.setProperty('--top-nav-height', `${top}px`);
            },
            { immediate: true },
        );

        onMounted(() => {
            useResizeObserver(pendoPage, (entries) => {
                const entry = entries[0];
                const { width: pendoPageWidth } = entry.contentRect;
                setPendoPageRightMargin(pendoPageWidth);
            });
        });

        return {
            pendoPage,
        };
    },
    data () {
        return {
            appAgeInterval: null,
            daysOutOfDate: 0,
            panelOpen: false,
            panelContent: () => null,
        };
    },
    computed: {
        ...mapPiniaState(useAuthStore, {
            authenticated: (store) => store.authenticated,
        }),
        ...mapPiniaState(useLeftNavStore, ['isTopBannerDismissed']),
        isOnGlobalSettings () {
            return isOnGlobalSettingsPage(this.$route);
        },
        isOnTrialExpiredRoute () {
            return this.$route?.name === 'TrialExpired';
        },
        canHaveGlobalBanner () {
            const params = {
                isTopBannerDismissed: this.isTopBannerDismissed,
                currentRoute: this.$route?.name,
            };

            return canHaveGlobalBanner(params);
        },
        routerViewKey () {
            // This is a workaround to prevent the router-view from remounting when navigating
            // between different pages in the Listen module
            if (isOnListenPage(this.$route)) {
                return 'pendo-listen';
            }
            // This is a workaround to prevent the router-view from remounting when navigating
            // between replays in the queue
            if (isOnReplayPlayerPage(this.$route.path)) {
                return 'replay-player';
            }
            // This is a workaround to prevent the router-view from remounting when updating
            // the URL based on progress through the data-sync setup flow
            if (isOnDataSyncPage(this.$route.path)) {
                return 'data-sync';
            }

            return this.$route?.path || 'pendo-app';
        },
        showInstallSnippetBanner () {
            return !this.$productLine(PRODUCT_LINES.ADOPT);
        },
        isAppFrameActive () {
            return shouldShowNavItems(this.$route, this.authenticated);
        },
        showAncientAppVersionBanner () {
            return this.daysOutOfDate > 6;
        },
    },
    watch: {
        '$route.path': {
            handler (newPath, oldPath) {
                // Close any open SidePanel on navigation — the panel is scoped
                // to the page that opened it (activity blade, event-property
                // blade, etc.), so leaving it up after route change is
                // disorienting. `oldPath` is undefined on the immediate call.
                if (oldPath && newPath !== oldPath && this.panelOpen) {
                    this.onCloseSidePanel();
                }
                setTimeout(() => {
                    const routerViewWrapper = this.$refs.routerViewWrapper?.$el || this.$refs.routerViewWrapper;

                    if (!routerViewWrapper) {
                        return;
                    }

                    // focus the first heading if it exists
                    const firstHeading = routerViewWrapper.querySelector?.('h1');

                    if (firstHeading) {
                        firstHeading.setAttribute('tabindex', '-1');
                        firstHeading.style.outline = 'none';
                        firstHeading.focus();

                        return;
                    }

                    // focus the first tabbable element if it exists
                    const [firstTabbableElement] = tabbable(routerViewWrapper);

                    if (firstTabbableElement) {
                        firstTabbableElement.focus();

                        return;
                    }

                    // focus the router-view-wrapper if no other element is focusable
                    routerViewWrapper.setAttribute('tabindex', '-1');
                    routerViewWrapper.focus();
                }, 200);
            },
            flush: 'post',
            immediate: true,
        },
    },
    created () {
        EventBus.$on('openSidePanel', this.onOpenSidePanel);
        EventBus.$on('closeSidePanel', this.onCloseSidePanel);
        EventBus.$on('sidePanelClosed', this.onSidePanelClosed);
        EventBus.$on('closeTopBanner', this.onCloseTopBanner);
    },
    mounted () {
        if (!this.canHaveGlobalBanner) {
            this.setTopBannerHeight({ topBannerHeight: 0 });
        }

        const fourHours = 60 * 1000 * 60 * 4;
        const oneDay = 60 * 1000 * 60 * 24;

        this.appAgeInterval = window.setInterval(() => {
            const timeDiff = Date.now() - window.__appLoadDate.getTime();
            this.daysOutOfDate = parseInt(timeDiff / oneDay);
        }, fourHours);
    },
    beforeUnmount () {
        window.clearInterval(this.appAgeInterval);
        EventBus.$off('openSidePanel', this.onOpenSidePanel);
        EventBus.$off('closeSidePanel', this.onCloseSidePanel);
        EventBus.$off('sidePanelClosed', this.onSidePanelClosed);
        EventBus.$off('closeTopBanner', this.onCloseTopBanner);
    },
    methods: {
        ...mapPiniaActions(useLeftNavStore, ['setTopBannerDismissed', 'setTopBannerHeight']),
        onOpenSidePanel (renderPanel) {
            const wasAlreadyOpen = this.panelOpen;
            this.panelOpen = true;
            this.panelContent = renderPanel;
            this.$nextTick(() => {
                setTimeout(() => {
                    EventBus.$emit('focusSidePanel');
                }, wasAlreadyOpen ? 50 : 400);
            });
        },
        onCloseSidePanel () {
            this.panelOpen = false;
        },
        onSidePanelClosed () {
            this.panelOpen = false;
        },
        onCloseTopBanner () {
            this.setTopBannerDismissed({ isTopBannerDismissed: true });
        },
    },
};
</script>

<style lang="less" scoped>
.vue-wrapper {
    display: flex !important;
    flex-direction: column;
    flex-grow: 1;
    position: relative !important;
    inset: unset !important;

    @panel-width: 377px;
    .slide-panel-enter-active,
    .slide-panel-leave-active {
        transition: transform 0.3s;
    }

    .slide-panel-enter-to,
    .slide-panel-leave-from {
        transform: translateX(0);
    }

    .slide-panel-enter-from,
    .slide-panel-leave-to {
        transform: translateX(@panel-width);
    }

    .pendo-page {
        transition: margin-right 220ms;
        position: relative !important;
    }

    .side-panel-wrapper {
        z-index: 1;
        background-color: var(--overlay-modal-background-base);
        width: @panel-width;
        position: fixed;
        right: 0;
        top: var(--drawer-top-position);
        bottom: 0;
    }

    &.side-panel-open {
        .pendo-page {
            margin-right: @panel-width;
        }
    }

    &--framed {
        overflow: hidden;

        .side-panel-wrapper {
            position: absolute;
            top: 0;
        }
    }
}
</style>
